import axios from 'axios';
import lodash from 'lodash';
import { AxiosCustomRequestType } from '../../models/utils/axios.type';
import { BancoType, CEPType, CNPJType, EstadoType, MunicipioType } from '../../models/utils/brasilapi.type';
import { LocalStorageKey, SessionStorageKey } from '../../utils/constants.utils';

const instance = axios.create({
  method: 'get',
  baseURL: `https://brasilapi.com.br/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const call = async (request: AxiosCustomRequestType): Promise<any> => {
  try {
    const { status, data } = await instance.request({
      method: request.method,
      url: request.url,
      timeout: Number(request.timeout || 15000),
      data: request?.data,
      params: request?.params,
      headers: {
        ...(request.headers || {}),
      },
    });
    return { status, data };
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

const procurarCEP = async (cep: string): Promise<CEPType> => {
  const request: AxiosCustomRequestType = {
    method: 'GET',
    url: `cep/v2/${cep}`,
  };
  return (await call(request))?.data;
};

const procurarCNPJ = async (cnpj: string): Promise<CNPJType> => {
  const request: AxiosCustomRequestType = {
    method: 'GET',
    url: `cnpj/v1/${cnpj}`,
  };
  return (await call(request))?.data;
};

const procurarMunicipios = async (codigoEstado: string): Promise<MunicipioType[]> => {
  const sessionStorageKey = SessionStorageKey.BrasilAPICities.replace('{stateCode}', codigoEstado);
  const cities = JSON.parse(sessionStorage.getItem(sessionStorageKey) || 'null') as MunicipioType[];
  if (cities) {
    return cities;
  }

  const request: AxiosCustomRequestType = {
    method: 'GET',
    url: `ibge/municipios/v1/${codigoEstado}`,
  };
  const data = (await call(request))?.data as MunicipioType[];
  const transformedData = data
    .map((x) => ({ ...x, nome: lodash.startCase(x.nome.toLowerCase()) }))
    .sort((a: MunicipioType, b: MunicipioType) => {
      return a.nome.localeCompare(b.nome);
    });

  sessionStorage.setItem(sessionStorageKey, JSON.stringify(transformedData));
  return transformedData;
};

const listarBancos = async (): Promise<BancoType[]> => {
  const banks = JSON.parse(localStorage.getItem(LocalStorageKey.BrasilAPIBanks) || 'null') as BancoType[];
  if (banks) {
    return banks;
  }

  const request: AxiosCustomRequestType = {
    method: 'GET',
    url: `banks/v1`,
  };

  const data = (await call(request))?.data as BancoType[];
  const sortedData = data.sort((a: BancoType, b: BancoType) => {
    const aValue = a.code || 999;
    const bValue = b.code || 999;
    if (aValue === bValue) {
      return 0;
    }
    return aValue > bValue ? 1 : -1;
  });

  localStorage.setItem(LocalStorageKey.BrasilAPIBanks, JSON.stringify(sortedData));
  return sortedData;
};

const listarEstados = async (): Promise<EstadoType[]> => {
  return [
    {
      id: 12,
      sigla: 'AC',
      nome: 'Acre',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 27,
      sigla: 'AL',
      nome: 'Alagoas',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 13,
      sigla: 'AM',
      nome: 'Amazonas',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 16,
      sigla: 'AP',
      nome: 'Amapá',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 29,
      sigla: 'BA',
      nome: 'Bahia',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 23,
      sigla: 'CE',
      nome: 'Ceará',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 53,
      sigla: 'DF',
      nome: 'Distrito Federal',
      regiao: {
        id: 5,
        sigla: 'CO',
        nome: 'Centro-Oeste',
      },
    },
    {
      id: 32,
      sigla: 'ES',
      nome: 'Espírito Santo',
      regiao: {
        id: 3,
        sigla: 'SE',
        nome: 'Sudeste',
      },
    },
    {
      id: 52,
      sigla: 'GO',
      nome: 'Goiás',
      regiao: {
        id: 5,
        sigla: 'CO',
        nome: 'Centro-Oeste',
      },
    },
    {
      id: 21,
      sigla: 'MA',
      nome: 'Maranhão',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 31,
      sigla: 'MG',
      nome: 'Minas Gerais',
      regiao: {
        id: 3,
        sigla: 'SE',
        nome: 'Sudeste',
      },
    },
    {
      id: 50,
      sigla: 'MS',
      nome: 'Mato Grosso do Sul',
      regiao: {
        id: 5,
        sigla: 'CO',
        nome: 'Centro-Oeste',
      },
    },
    {
      id: 51,
      sigla: 'MT',
      nome: 'Mato Grosso',
      regiao: {
        id: 5,
        sigla: 'CO',
        nome: 'Centro-Oeste',
      },
    },
    {
      id: 15,
      sigla: 'PA',
      nome: 'Pará',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 25,
      sigla: 'PB',
      nome: 'Paraíba',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 26,
      sigla: 'PE',
      nome: 'Pernambuco',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 22,
      sigla: 'PI',
      nome: 'Piauí',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 41,
      sigla: 'PR',
      nome: 'Paraná',
      regiao: {
        id: 4,
        sigla: 'S',
        nome: 'Sul',
      },
    },
    {
      id: 33,
      sigla: 'RJ',
      nome: 'Rio de Janeiro',
      regiao: {
        id: 3,
        sigla: 'SE',
        nome: 'Sudeste',
      },
    },
    {
      id: 24,
      sigla: 'RN',
      nome: 'Rio Grande do Norte',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 11,
      sigla: 'RO',
      nome: 'Rondônia',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 14,
      sigla: 'RR',
      nome: 'Roraima',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
    {
      id: 43,
      sigla: 'RS',
      nome: 'Rio Grande do Sul',
      regiao: {
        id: 4,
        sigla: 'S',
        nome: 'Sul',
      },
    },
    {
      id: 42,
      sigla: 'SC',
      nome: 'Santa Catarina',
      regiao: {
        id: 4,
        sigla: 'S',
        nome: 'Sul',
      },
    },
    {
      id: 28,
      sigla: 'SE',
      nome: 'Sergipe',
      regiao: {
        id: 2,
        sigla: 'NE',
        nome: 'Nordeste',
      },
    },
    {
      id: 35,
      sigla: 'SP',
      nome: 'São Paulo',
      regiao: {
        id: 3,
        sigla: 'SE',
        nome: 'Sudeste',
      },
    },
    {
      id: 17,
      sigla: 'TO',
      nome: 'Tocantins',
      regiao: {
        id: 1,
        sigla: 'N',
        nome: 'Norte',
      },
    },
  ];

  // const states = JSON.parse(localStorage.getItem(LocalStorageKey.BrasilAPIStates) || 'null') as EstadoType[];
  // if (states) {
  //   return states;
  // }

  // const request: AxiosCustomRequestType = {
  //   method: 'GET',
  //   url: `ibge/uf/v1`,
  // };

  // const data = (await call(request))?.data as EstadoType[];
  // const sortedData = data.sort((a: EstadoType, b: EstadoType) => {
  //   return a.nome.localeCompare(b.nome);
  // });
  // localStorage.setItem(LocalStorageKey.BrasilAPIStates, JSON.stringify(sortedData));
  // return sortedData;
};

export { procurarCEP, procurarCNPJ, procurarMunicipios, listarBancos, listarEstados };
